import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';

// Local image paths
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const Welcome = () => {
  const navigate = useNavigate();
  const [showPackageButtons, setShowPackageButtons] = useState(false);
  const packageGridRef = useRef(null);

  useEffect(() => {
    if (showPackageButtons) {
      // Wait for the grid to render, then scroll all the way to the bottom so the footer is visible
      const id = window.setTimeout(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      }, 80);
      return () => window.clearTimeout(id);
    }
    // When collapsing, scroll back to the top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return undefined;
  }, [showPackageButtons]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Logo Section - Both logos centered */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img
            src={LOGO_URL}
            alt="TCA Auditors Logo"
            className="h-24 sm:h-32 w-auto object-contain select-none"
            draggable="false"
            data-testid="tca-logo"
          />
          {/* Banner logo */}
          <img
            src={BANNER_URL}
            alt="Tax & Auditing Consultants - For Businesses & Individuals"
            className="h-[4.5rem] sm:h-24 w-auto object-contain"
            data-testid="tca-banner"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-8 sm:mb-10 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Selection Card */}
        <Card className="w-full max-w-4xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10">
            {/* Introduction Paragraphs */}
            <div className="space-y-4 text-sm sm:text-base text-foreground leading-relaxed mb-8">
              <p>
                Running a business means wearing too many hats — and tax compliance, VAT submissions, payroll and annual financial statements should not be one of them. An accounting retainer is a single, fixed monthly fee that puts a team of qualified accountants permanently in your corner: no surprise invoices, no scrambling at year-end, and no uncertainty about whether your numbers are right. You know exactly what you pay, exactly what is covered, and exactly who is responsible.
              </p>
              <p>
                While you focus on growing your business, our CA(SA)-led team handles every return, every deadline and every SARS query — giving you the peace of mind that your compliance and tax affairs are in the hands of professionals who genuinely care about your bottom line.
              </p>
              <p>
                Nine fixed-fee packages across three tiers — Starter, Core and Elite — so you know exactly what compliance, accounting and advisory you are getting.
              </p>
            </div>

            {/* Selection Heading */}
            <div className="pb-6">
              <h2 className="text-lg font-semibold text-tca-blue inline-block underline-tca-blue" data-testid="welcome-heading">
                To start, please select one of the following options:
              </h2>
            </div>

            {/* Three Horizontal Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-4 justify-center items-stretch">
              <Button
                data-testid="btn-comparison-table"
                className="btn-gold-glow flex-1 bg-accent hover:bg-accent text-accent-foreground font-semibold py-3 px-6 h-auto min-h-[48px] text-sm sm:text-base rounded-lg shadow-soft whitespace-normal leading-tight"
                onClick={() => navigate('/page-2')}
              >
                Show me a detailed Comparison Table of all the Retainer Packages
              </Button>
              <Button
                data-testid="btn-help-choose"
                className="btn-gold-glow flex-1 bg-accent hover:bg-accent text-accent-foreground font-semibold py-3 px-6 h-auto min-h-[48px] text-sm sm:text-base rounded-lg shadow-soft whitespace-normal leading-tight"
                onClick={() => window.open('https://tcauditors.co.za/pricing/matchmaker', '_blank')}
              >
                Help me Choose a suitable Retainer Package
              </Button>
              <Button
                data-testid="btn-know-package"
                className="btn-gold-glow flex-1 bg-accent hover:bg-accent text-accent-foreground font-semibold py-3 px-6 h-auto min-h-[48px] text-sm sm:text-base rounded-lg shadow-soft whitespace-normal leading-tight"
                onClick={() => setShowPackageButtons(!showPackageButtons)}
              >
                I know which Retainer Package suits my Business
              </Button>
            </div>

            {/* 9 Package Buttons - revealed on 1st button click */}
            {showPackageButtons && (
              <div ref={packageGridRef} className="mt-8 space-y-[1.6rem] animate-fade-in" data-testid="package-buttons-grid">
                <p className="text-sm font-semibold text-tca-blue text-center mb-4">
                  Please select your package:
                </p>

                {/* Starter Row */}
                <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                  <p className="text-[1.06rem] font-extrabold uppercase tracking-[0.18em] text-foreground">Starter</p>
                  <p className="text-xs text-muted-foreground -mt-1 mb-1">For Compiled Financial Statements (Non-VAT companies)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Button
                      data-testid="btn-starter-bronze"
                      onClick={() => navigate('/starter-bronze')}
                      className="bg-[#8B5E3C] hover:bg-[#8B5E3C] text-white font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(139,94,60,0.7),0_0_40px_rgba(139,94,60,0.5),0_0_60px_rgba(139,94,60,0.3)] hover:z-10"
                    >
                      Starter Bronze
                    </Button>
                    <Button
                      data-testid="btn-starter-silver"
                      onClick={() => navigate('/starter-silver')}
                      className="bg-gradient-to-b from-[#C0C0C0] via-[#E8E8E8] to-[#A8A8A8] hover:from-[#C0C0C0] hover:via-[#E8E8E8] hover:to-[#A8A8A8] text-gray-800 font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(192,192,192,0.8),0_0_40px_rgba(192,192,192,0.5),0_0_60px_rgba(192,192,192,0.3)] hover:z-10 border border-[#B0B0B0]"
                    >
                      Starter Silver
                    </Button>
                    <Button
                      data-testid="btn-starter-gold"
                      onClick={() => navigate('/starter-gold')}
                      className="bg-[#B8860B] hover:bg-[#B8860B] text-white font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(184,134,11,0.7),0_0_40px_rgba(184,134,11,0.5),0_0_60px_rgba(184,134,11,0.3)] hover:z-10"
                    >
                      Starter Gold
                    </Button>
                  </div>
                </div>

                {/* Core Row */}
                <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                  <p className="text-[1.06rem] font-extrabold uppercase tracking-[0.18em] text-foreground">Core</p>
                  <p className="text-xs text-muted-foreground -mt-1 mb-1">For Reviewed Financial Statements (VAT-registered companies)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Button
                      data-testid="btn-core-bronze"
                      onClick={() => navigate('/core-bronze')}
                      className="bg-[#8B5E3C] hover:bg-[#8B5E3C] text-white font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(139,94,60,0.7),0_0_40px_rgba(139,94,60,0.5),0_0_60px_rgba(139,94,60,0.3)] hover:z-10"
                    >
                      Core Bronze
                    </Button>
                    <Button
                      data-testid="btn-core-silver"
                      onClick={() => navigate('/core-silver')}
                      className="bg-gradient-to-b from-[#C0C0C0] via-[#E8E8E8] to-[#A8A8A8] hover:from-[#C0C0C0] hover:via-[#E8E8E8] hover:to-[#A8A8A8] text-gray-800 font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(192,192,192,0.8),0_0_40px_rgba(192,192,192,0.5),0_0_60px_rgba(192,192,192,0.3)] hover:z-10 border border-[#B0B0B0]"
                    >
                      Core Silver
                    </Button>
                    <Button
                      data-testid="btn-core-gold"
                      onClick={() => navigate('/core-gold')}
                      className="bg-[#B8860B] hover:bg-[#B8860B] text-white font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(184,134,11,0.7),0_0_40px_rgba(184,134,11,0.5),0_0_60px_rgba(184,134,11,0.3)] hover:z-10"
                    >
                      Core Gold
                    </Button>
                  </div>
                </div>

                {/* Elite Row */}
                <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                  <p className="text-[1.06rem] font-extrabold uppercase tracking-[0.18em] text-foreground">Elite</p>
                  <p className="text-xs text-muted-foreground -mt-1 mb-1">For Audited Financial Statements (either Non-VAT or VAT registered companies)</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Button
                      data-testid="btn-elite-bronze"
                      onClick={() => navigate('/elite-bronze')}
                      className="bg-[#8B5E3C] hover:bg-[#8B5E3C] text-white font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(139,94,60,0.7),0_0_40px_rgba(139,94,60,0.5),0_0_60px_rgba(139,94,60,0.3)] hover:z-10"
                    >
                      Elite Bronze
                    </Button>
                    <Button
                      data-testid="btn-elite-silver"
                      onClick={() => navigate('/elite-silver')}
                      className="bg-gradient-to-b from-[#C0C0C0] via-[#E8E8E8] to-[#A8A8A8] hover:from-[#C0C0C0] hover:via-[#E8E8E8] hover:to-[#A8A8A8] text-gray-800 font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(192,192,192,0.8),0_0_40px_rgba(192,192,192,0.5),0_0_60px_rgba(192,192,192,0.3)] hover:z-10 border border-[#B0B0B0]"
                    >
                      Elite Silver
                    </Button>
                    <Button
                      data-testid="btn-elite-gold"
                      onClick={() => navigate('/elite-gold')}
                      className="bg-[#B8860B] hover:bg-[#B8860B] text-white font-semibold py-3 px-4 h-auto min-h-[44px] text-sm rounded-lg transition-all duration-200 hover:scale-[1.15] hover:shadow-[0_0_20px_rgba(184,134,11,0.7),0_0_40px_rgba(184,134,11,0.5),0_0_60px_rgba(184,134,11,0.3)] hover:z-10"
                    >
                      Elite Gold
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={1} totalPages={3} showTotal={true} pageTitle="Welcome" />
        <img
          src={TCA_SHORT_LOGO}
          alt="TCA Logo"
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>
    </div>
  );
};

export default Welcome;
